import { useState, useMemo, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';
import { useAuth } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';

import { toaster } from '~/shared/utils/toaster';
import StorageIcon from '~/shared/assets/icons/storage.svg';
import CurrentUtilizationIcon from '~/shared/assets/icons/current-utilization.svg';
import ImpactedCapacityIcon from '~/shared/assets/icons/impacted-capacity.svg';
import StorageIconInactive from '~/shared/assets/icons/total-volumes-inactive.svg';
import ImpactedCapacityIconInactive from '~/shared/assets/icons/impacted-storage-inactive.svg';
import CurrentUtilizationIconInactive from '~/shared/assets/icons/utilization-inactive.svg';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';
import SidePanel from '~/shared/components/SidePanel';
import SearchBar from '~/shared/components/SearchBar';
import CloseIcon from '~/shared/assets/icons/arrow-right.svg';
import EditIcon from '~/shared/assets/icons/edit.svg';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import Tabs, { Tab } from '~/shared/components/Tabs';

import RuleCondition from './RuleCondition';
import MatchingVolumes from './MatchingVolumes';
import PotentialVolumes from './PotentialVolumes';
import Exceptions from './Exceptions';
import CreateExceptionModal from './CreateExceptionModal';
import RemoveExceptionModal from './RemoveExceptionModal';
import DeleteRuleModal from './DeleteRuleModal';
import ToggleRuleModal from './ToggleRuleModal';
import OptimisticToggleSwitch from './OptimisticToggleSwitch';
import { useRuleDryRun } from './hooks/useRuleDryRun';
import { useToggleRule } from './hooks/useToggleRule';
import { useAutoscaleRuleDetails } from './hooks/useAutoscaleRuleDetails';

import type { AutoscaleRule } from '../hooks/useAutoscaleRulesQuery';
import type { Volume } from '~/shared/types/api';

interface AutoscaleRuleDetailsSidePanelProps {
  rule: AutoscaleRule | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (ruleId: string, rawRule: Record<string, unknown>) => void;
}

interface KPICardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
  isLoading?: boolean;
}

type TabValue = 'ruleCondition' | 'matchingVolumes' | 'potentialVolumes' | 'exceptions';

function formatCapacity(bytes: number): string {
  if (!bytes || bytes === 0) {
    return '0 GiB';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const unitIndex = Math.min(i, sizes.length - 1);
  const convertedValue = bytes / Math.pow(k, unitIndex);

  return `${convertedValue.toFixed(1)} ${sizes[unitIndex]}`;
}

function SkeletonLoader({ width, className }: { width: number; className?: string }) {
  return (
    <Skeleton
      containerClassName="block leading-[unset!important]"
      className={clsx('h-[24px] 2xl:h-[24px] leading-[unset!important] rounded-[8px]', className)}
      baseColor="var(--color-gray-200)"
      highlightColor="#FFFF"
      width={width}
    />
  );
}

function KPICard({ icon: Icon, title, text, isLoading = false }: KPICardProps) {
  return (
    <div className="flex items-center p-[16px] 2xl:px-5 2xl:py-[24px]  gap-2 w-full  shadow-card rounded-[12px] border border-primary-100">
      <div className="size-9">{Icon && <Icon className="size-9!" />}</div>
      <div className="flex flex-col gap-[3px]">
        <Typography variant="p3-reg" className="" fontWeight={500}>
          {title}{' '}
        </Typography>
        <div className="flex gap-2">
          {isLoading ? (
            <SkeletonLoader width={40} />
          ) : (
            <Typography variant="H3-resp" fontWeight={500} color="text-[#2D2D32]">
              {text}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AutoscaleRuleDetailsSidePanel({
  rule,
  isOpen,
  onClose,
  onEdit,
}: AutoscaleRuleDetailsSidePanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isUserReadOnly = !!user?.roles?.find((r) => r.key === 'ReadOnly');
  // const api = useXFetch();
  const [activeTab, setActiveTab] = useState<TabValue>('ruleCondition');
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [isRemoveExceptionModalOpen, setIsRemoveExceptionModalOpen] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState<string | null>(null);
  const [selectedVolumeName, setSelectedVolumeName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVolumeType, setSelectedVolumeType] = useState<'supported' | 'notSupported'>(
    'supported'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [modalIntentTurningOn, setModalIntentTurningOn] = useState<boolean>(false);
  const [isExceptionOperationPending, setIsExceptionOperationPending] = useState(false);

  // Only fetch dry run data if we don't have cached volume data from the table query
  const shouldFetchDryRun = isOpen && !!rule && !rule.volumeData;
  const { data: dryRunData, isLoading: isDryRunLoading } = useRuleDryRun(
    rule?.rawRule ?? null,
    shouldFetchDryRun
  );

  const { data: ruleDetails } = useAutoscaleRuleDetails(rule?.ruleId ?? null, isOpen && !!rule);

  // Use cached volume data from table if available, otherwise use fresh dry run data
  const volumeData = useMemo(() => {
    if (rule?.volumeData) {
      return {
        datafiable: {
          volumes: rule.volumeData.datafiable,
          count: rule.volumes,
          totalCapacity: rule.capacity,
          averageUtilization: parseFloat(rule.utilization) || 0,
        },
        potentiallyDatafiable: {
          volumes: rule.volumeData.potentiallyDatafiable,
          count: rule.volumeData.potentiallyDatafiable?.length ?? 0,
          totalCapacity: 0,
          averageUtilization: 0,
        },
        excluded: {
          volumes: rule.volumeData.excluded,
          count: rule.exceptions,
          totalCapacity: 0,
          averageUtilization: 0,
        },
      };
    }
    return dryRunData;
  }, [rule, dryRunData]);

  // const isLoadingVolumes = shouldFetchDryRun && isDryRunLoading;
  const showKPISkeleton = isDryRunLoading || isExceptionOperationPending;

  const calculatedStats = useMemo(() => {
    if (!volumeData) {
      return {
        totalVolumes: ruleDetails?.volumes ?? rule?.volumes ?? 0,
        totalCapacity: ruleDetails?.capacity ?? rule?.capacity ?? 0,
        averageUtilization: ruleDetails?.utilization ?? rule?.utilization ?? '0%',
      };
    }

    const totalVolumes = volumeData.datafiable.count || 0;

    const totalCapacity = volumeData.datafiable.totalCapacity || 0;

    const averageUtilization = volumeData.datafiable.averageUtilization || 0;

    return {
      totalVolumes,
      totalCapacity,
      averageUtilization: `${averageUtilization.toFixed(2)}%`,
    };
  }, [volumeData, ruleDetails, rule]);

  const handleSuccessWithRefresh = async ({
    message,
    title,
  }: {
    message: string;
    title: string;
  }) => {
    setIsExceptionOperationPending(true);

    try {
      if (rule?.ruleId) {
        await queryClient.invalidateQueries({
          queryKey: ['autoscaleRuleDetails', rule.ruleId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['ruleDryRun', rule.rawRule],
        });
        await queryClient.invalidateQueries({
          queryKey: ['autoscaleRules'],
          exact: false,
        });
      }

      toaster({
        variant: 'success',
        title: title,
        message: message,
      });
    } finally {
      setTimeout(() => setIsExceptionOperationPending(false), 500);
    }
  };

  const handleCreateException = (volumeId: string) => {
    setSelectedVolumeId(volumeId);
    setIsExceptionModalOpen(true);
  };

  const handleCloseExceptionModal = () => {
    setIsExceptionModalOpen(false);
    setSelectedVolumeId(null);
  };

  const handleRemoveException = (volumeId: string, volumeName?: string) => {
    setSelectedVolumeId(volumeId);
    setSelectedVolumeName(volumeName || null);
    setIsRemoveExceptionModalOpen(true);
  };

  const handleCloseRemoveExceptionModal = () => {
    setIsRemoveExceptionModalOpen(false);
    setSelectedVolumeId(null);
    setSelectedVolumeName(null);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteSuccess = () => {
    onClose();
  };

  const matchingVolumesCount = volumeData?.datafiable.count ?? 0;
  const potentialVolumesCount = volumeData?.potentiallyDatafiable.count ?? 0;
  const exceptionsCount = volumeData?.excluded.count ?? 0;

  const tabs: Tab<TabValue>[] = useMemo(
    () => [
      {
        label: 'Rule Condition',
        value: 'ruleCondition',
      },
      {
        label: 'Matching Volumes',
        value: 'matchingVolumes',
        right: showKPISkeleton ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
            {matchingVolumesCount}
          </p>
        ),
      },
      {
        label: 'Potential Volumes',
        value: 'potentialVolumes',
        right: showKPISkeleton ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
            {potentialVolumesCount}
          </p>
        ),
      },
      {
        label: 'Exceptions',
        value: 'exceptions',
        right: showKPISkeleton ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <div className="flex gap-1">
            <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
              {exceptionsCount}
            </p>
          </div>
        ),
      },
    ],
    [matchingVolumesCount, potentialVolumesCount, exceptionsCount, showKPISkeleton]
  );

  const { toggleRule, isMutating } = useToggleRule({
    onSuccess: () => {
      setIsToggleModalOpen(false);
    },
    onError: () => {
      setIsToggleModalOpen(false);
    },
  });

  const handleSwitchClick = (newActiveState: boolean) => {
    if (!rule) {
      return;
    }
    if (newActiveState) {
      toggleRule({ rule, newActiveState: true });
    } else {
      // If turning off, show confirmation modal
      setModalIntentTurningOn(newActiveState);
      setIsToggleModalOpen(true);
    }
  };

  const handleToggleConfirm = () => {
    if (!rule) {
      return;
    }
    toggleRule({ rule, newActiveState: modalIntentTurningOn });
  };

  const handleCloseToggleModal = () => {
    setIsToggleModalOpen(false);
  };

  const handleEdit = () => {
    if (rule && onEdit) {
      onEdit(rule.ruleId, rule.rawRule);
      onClose();
    }
  };

  function Header({ rule }: { rule: AutoscaleRule }) {
    // The source of truth for the enabled state is the react-query cache.
    // We prioritize the `ruleDetails` from `useAutoscaleRuleDetails` if available,
    // otherwise we fall back to the `rule` prop passed from the table.
    const isEnabled = ruleDetails?.enabled ?? rule.enabled;

    return (
      <>
        <div className="flex items-center justify-between ">
          <OptimisticToggleSwitch
            switchState={isEnabled}
            onSwitchClick={handleSwitchClick}
            isPending={isMutating}
            isUserReadOnly={isUserReadOnly}
          />
          <div className="flex items-center gap-2">
            <Button className="w-10 " variant="third" icon={<EditIcon />} onClick={handleEdit} />
            <Button
              className="w-10 hover:!border-red group"
              variant="third"
              icon={
                <DeleteIcon className="size-5 group-hover:text-red transition-colors duration-300" />
              }
              onClick={handleOpenDeleteModal}
            />
            <Button
              // size="md-responsive"
              className="w-10"
              variant="third"
              icon={<CloseIcon className="size-5" />}
              onClick={onClose}
            />
          </div>
        </div>

        <div className="py-2.5 flex flex-col gap-[18px] font-family-outfit">
          <div className="flex flex-col gap-[18px] font-family-outfit">
            <Typography variant="H3-med" className="text-2xl 2xl:text-[28px] font-[500] ">
              {rule.ruleId}
            </Typography>
          </div>
          {/* <div className="flex divide-x-2 divide-gray-400 ">
            <Typography variant="p2-med" fontWeight={400} color="text-gray-700 pr-2">
              {tenants?.activeTenant?.name || rule.account}
            </Typography>
            <Typography variant="p2-med" fontWeight={400} color="text-gray-700 my-auto px-2">
              Created on 12/03/2025
            </Typography>
            <div className="flex items-center gap-2 px-2">
              <UserAvatar username={user?.name || ''} size="sm" />
              <Typography variant="p2-med" fontWeight={400} color="text-gray-700">
                {user?.name}
              </Typography>
            </div>
          </div> */}
        </div>
      </>
    );
  }

  return (
    <>
      <SidePanel
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        width="w-[790px]"
        excludeSelectors={[
          '.ag-row',
          '.ag-cell',
          '.auto-scale-rule-dialog',
          '.auto-scale-rule-dialog-backdrop',
          '[role="dialog"]',
          '[role="menu"]',
        ]}
      >
        {!rule ? (
          <div className="flex flex-col h-full items-center justify-center">
            <Typography variant="p3-reg" color="text-gray-500">
              No rule selected
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col h-full p-6">
            <Header rule={rule} />

            <div className="py-3 flex gap-2">
              <KPICard
                icon={ruleDetails?.enabled ?? rule.enabled ? StorageIcon : StorageIconInactive}
                title={`Total volume${calculatedStats?.totalVolumes === 1 ? '' : 's'}`}
                text={`${calculatedStats.totalVolumes}`}
                isLoading={showKPISkeleton}
              />
              <KPICard
                icon={
                  ruleDetails?.enabled ?? rule.enabled
                    ? ImpactedCapacityIcon
                    : ImpactedCapacityIconInactive
                }
                title="Impacted capacity"
                text={formatCapacity(calculatedStats.totalCapacity)}
                isLoading={showKPISkeleton}
              />
              <KPICard
                icon={
                  ruleDetails?.enabled ?? rule.enabled
                    ? CurrentUtilizationIcon
                    : CurrentUtilizationIconInactive
                }
                title="Utilization"
                text={calculatedStats.averageUtilization}
                isLoading={showKPISkeleton}
              />
            </div>

            <div className="mb4">
              <Tabs
                tabClassName="group"
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
            </div>

            {/* Search - Only show for volume tabs */}
            {(activeTab === 'matchingVolumes' ||
              activeTab === 'potentialVolumes' ||
              activeTab === 'exceptions') && (
              <div className=" py-5 items-center mb5">
                <SearchBar
                  size="md"
                  className="flex-1 w-full"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {activeTab === 'ruleCondition' && <RuleCondition conditions={rule.conditions} />}
            {activeTab === 'matchingVolumes' && (
              <MatchingVolumes
                volumes={(volumeData?.datafiable.volumes as Volume[]) ?? []}
                onCreateException={handleCreateException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={showKPISkeleton}
              />
            )}
            {activeTab === 'potentialVolumes' && (
              <PotentialVolumes
                volumes={(volumeData?.potentiallyDatafiable.volumes as Volume[]) ?? []}
                onCreateException={handleCreateException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={showKPISkeleton}
              />
            )}
            {activeTab === 'exceptions' && (
              <Exceptions
                volumes={(volumeData?.excluded.volumes as Volume[]) ?? []}
                onCreateException={handleCreateException}
                onRemoveException={handleRemoveException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={showKPISkeleton}
              />
            )}
          </div>
        )}
      </SidePanel>

      <CreateExceptionModal
        isOpen={isExceptionModalOpen && !!rule && !!selectedVolumeId}
        onClose={handleCloseExceptionModal}
        volumeId={selectedVolumeId || ''}
        ruleId={rule?.ruleId || ''}
        onSuccess={async () => {
          await handleSuccessWithRefresh({
            message: `Exception for ${selectedVolumeId} has been created. This volume will no longer be autoscaled.`,
            title: 'Exception created',
          });
        }}
      />

      <RemoveExceptionModal
        isOpen={isRemoveExceptionModalOpen && !!rule && !!selectedVolumeId}
        onClose={handleCloseRemoveExceptionModal}
        volumeId={selectedVolumeId || ''}
        ruleId={rule?.ruleId || ''}
        volumeName={selectedVolumeName || undefined}
        onSuccess={async () => {
          await handleSuccessWithRefresh({
            message: `Exception for ${selectedVolumeId} has been cleared`,
            title: 'Rule behavior restored',
          });
        }}
      />

      {rule && (
        <DeleteRuleModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          rule={rule}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}

      <ToggleRuleModal
        isOpen={isToggleModalOpen}
        onClose={handleCloseToggleModal}
        onConfirm={handleToggleConfirm}
        isTurningOn={modalIntentTurningOn}
        isPending={isMutating}
      />
    </>
  );
}
